import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { Toast } from 'primereact/toast';
import { Dropdown } from 'primereact/dropdown';
import { AutoComplete } from 'primereact/autocomplete';
import '../../css/Pages.css';
import { downloadExcel } from '../../utils/excelUtils';

const superCleanString = (str) => {
    if (!str) return '';
    return str
        .toString()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .replace(/đ/g, 'd')
        .replace(/Đ/g, 'D')
        .replace(/\s+/g, ' ')
        .toLowerCase()
        .trim();
};

const SearchableFocusDropdown = ({ value, options, onChange, placeholder, disabled, panelUniqueClass, className, ...rest }) => {
    const dropdownRef = useRef(null);
    const focusTrapRef = useRef(null);

    const handleShow = () => {
        setTimeout(() => {
            try {
                const panel = document.querySelector(`.${panelUniqueClass}`);
                if (panel) {
                    const filterInput = panel.querySelector('.p-dropdown-filter');
                    if (filterInput && typeof filterInput.focus === 'function') {
                        filterInput.focus();

                        focusTrapRef.current = (e) => {
                            if (e.target !== filterInput) {
                                filterInput.focus();
                            }
                        };

                        panel.addEventListener('focusin', focusTrapRef.current);
                    }
                }
            } catch (err) {
                console.warn("Focus error:", err);
            }
        }, 100);
    };

    const handleHide = () => {
        const panel = document.querySelector(`.${panelUniqueClass}`);
        if (panel && focusTrapRef.current) {
            panel.removeEventListener('focusin', focusTrapRef.current);
        }
    };

    return (
        <Dropdown
            ref={dropdownRef}
            value={value}
            options={options}
            onChange={onChange}
            filter
            placeholder={placeholder}
            className={className || "custom-dropdown"}
            panelClassName={panelUniqueClass}
            style={{ width: '100%' }}
            appendTo={document.body}
            onShow={handleShow}
            onHide={handleHide}
            disabled={disabled}
            {...rest}
        />
    );
};

const ProposalStatsByIndividual = () => {
    const toast = useRef(null);
    const [isLoading, setIsLoading] = useState(true);

    const [departments, setDepartments] = useState([]);
    const [topicLevels, setTopicLevels] = useState([]);
    const [proposals, setProposals] = useState([]);
    const [users, setUsers] = useState([]);

    const [filterDept, setFilterDept] = useState('');
    const [filterAuthorId, setFilterAuthorId] = useState(null);
    const [filterTopicLevel, setFilterTopicLevel] = useState('');
    const [filterYear, setFilterYear] = useState('');
    const [globalSearch, setGlobalSearch] = useState('');
    const [filterStatus, setFilterStatus] = useState('');

    const [selectedAuthor, setSelectedAuthor] = useState(null);
    const [filteredAuthors, setFilteredAuthors] = useState([]);

    const [currentPage, setCurrentPage] = useState(1);
    const itemsPerPage = 10;

    const [exportDropdownOpen, setExportDropdownOpen] = useState(false);
    const [isExportModalOpen, setIsExportModalOpen] = useState(false);
    const [previewExportData, setPreviewExportData] = useState(null);
    const [exportFilters, setExportFilters] = useState({
        dept: '',
        userId: '',
        topicLevel: '',
        year: '2026',
        status: '',
        globalSearch: ''
    });

    const [isDesktop, setIsDesktop] = useState(true);

    useEffect(() => {
        const handleResize = () => setIsDesktop(window.innerWidth > 992);
        handleResize();
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const getAuthHeaders = useCallback(() => {
        const currentUser = JSON.parse(localStorage.getItem('user')) || {};
        return {
            'X-User-Role-Id': currentUser.RoleId ? currentUser.RoleId.toString() : '',
            'X-User-Dept-Id': currentUser.DepartmentId ? currentUser.DepartmentId.toString() : '',
            'Authorization': `Bearer ${localStorage.getItem('accessToken')}`
        };
    }, []);

    const handleLogout = useCallback(() => {
        toast.current?.show({ severity: 'error', summary: 'Lỗi', detail: 'Phiên đăng nhập hết hạn!', life: 3000 });
        setTimeout(() => {
            localStorage.clear();
            window.location.href = '/login';
        }, 1500);
    }, []);

    const fetchAllData = useCallback(async () => {
        setIsLoading(true);
        try {
            const baseUrl = process.env.REACT_APP_API_BASE_URL;
            const headers = getAuthHeaders();
            const currentUser = JSON.parse(localStorage.getItem('user')) || {};

            const userRoleName = (currentUser.RoleName || '').toLowerCase();
            const isAdmin = userRoleName === 'admin' || currentUser.RoleId === 1 || userRoleName.includes('trưởng khoa');

            const [deptRes, topicLevelRes, proposalRes, userRes] = await Promise.all([
                fetch(`${baseUrl}/department`, { headers }),
                fetch(`${baseUrl}/topiclevel`, { headers }),
                fetch(`${baseUrl}/topicproposal?userId=${currentUser.Id}&isAdmin=${isAdmin}&view=manage`, { headers }),
                fetch(`${baseUrl}/user`, { headers })
            ]);

            if ([deptRes, topicLevelRes, proposalRes, userRes].some(res => res.status === 401 || res.status === 403)) {
                return handleLogout();
            }

            if (deptRes.ok) setDepartments(await deptRes.json());
            if (topicLevelRes.ok) setTopicLevels(await topicLevelRes.json());
            if (proposalRes.ok) setProposals(await proposalRes.json());
            if (userRes.ok) setUsers(await userRes.json());

        } catch (error) {
            console.error("Lỗi tải dữ liệu:", error);
            toast.current?.show({ severity: 'error', summary: 'Lỗi', detail: 'Không thể tải dữ liệu thống kê!' });
        } finally {
            setIsLoading(false);
        }
    }, [getAuthHeaders, handleLogout]);

    useEffect(() => {
        fetchAllData();
    }, [fetchAllData]);

    const userOptions = useMemo(() => {
        return users.map(u => {
            const labelStr = `${u.LastName || ''} ${u.FirstName || ''} - ${u.Email || ''}`.trim();
            return {
                label: labelStr,
                searchKey: superCleanString(labelStr),
                value: u.Id
            };
        });
    }, [users]);

    const userFilterTemplate = (option) => {
        return (
            <div style={{ display: 'flex', flexDirection: 'column', padding: '4px 0' }}>
                <span style={{ fontWeight: 'bold', color: '#333' }}>{option.fullName}</span>
                <span style={{ fontSize: '12px', color: '#666' }}>
                    {option.email} {option.dept ? ` - ${option.dept}` : ''}
                </span>
            </div>
        );
    };

    const filterUserOptions = useMemo(() => {
        return users.map(u => {
            const fullName = `${u.LastName || ''} ${u.FirstName || ''}`.trim();
            const email = u.Email || '';
            const dept = u.DepartmentName || '';
            const rawSearchStr = `${fullName} ${email} ${dept}`;

            return {
                label: fullName,
                searchKey: superCleanString(rawSearchStr),
                value: u.Id,
                fullName: fullName,
                email: email,
                dept: dept
            };
        });
    }, [users]);

    const searchAuthor = (event) => {
        const query = superCleanString(event.query);
        let _filteredAuthors;

        if (!query.trim().length) {
            _filteredAuthors = [...filterUserOptions];
        } else {
            _filteredAuthors = filterUserOptions.filter((user) => {
                return user.searchKey.includes(query);
            });
        }
        setFilteredAuthors(_filteredAuthors);
    };

    const computeAggregatedData = useCallback((filters) => {
        if (!proposals.length) return [];

        let allowedDeptIds = [];
        if (filters.dept) {
            const selectedDeptId = filters.dept.toString();
            allowedDeptIds.push(selectedDeptId);

            const findChildren = (parentId) => {
                const children = departments.filter(d => d.ParentId?.toString() === parentId.toString());
                children.forEach(child => {
                    const childId = child.Id.toString();
                    if (!allowedDeptIds.includes(childId)) {
                        allowedDeptIds.push(childId);
                        findChildren(childId);
                    }
                });
            };

            findChildren(selectedDeptId);
        }

        let statsMap = new Map();

        const validProposals = proposals.filter(prop => {
            if (filters.status && prop.Status !== filters.status) return false;
            if (filters.topicLevel && prop.TopicLevelId?.toString() !== filters.topicLevel) return false;

            if (filters.year) {
                const year = prop.ProposalYear || new Date(prop.CreatedAt).getFullYear();
                if (year?.toString() !== filters.year.toString()) return false;
            }

            if (filters.globalSearch) {
                const query = filters.globalSearch.toLowerCase();
                const titleMatch = (prop.TopicName || '').toLowerCase().includes(query);
                const codeMatch = (prop.Code || '').toLowerCase().includes(query);
                if (!titleMatch && !codeMatch) return false;
            }

            if (filters.authorId || filters.userId) {
                const targetId = (filters.authorId || filters.userId).toString();
                let isMatch = prop.UserId?.toString() === targetId;
                if (!isMatch && prop.MembersJSON) {
                    try {
                        const mList = JSON.parse(prop.MembersJSON || '[]');
                        isMatch = mList.some(m => (m.UserId || m.userId || m.Id)?.toString() === targetId);
                    } catch (e) { }
                }
                if (!isMatch) return false;
            }

            return true;
        });

        validProposals.forEach(prop => {
            const userId = prop.UserId;
            if (!userId) return;

            if (filters.dept) {
                const propDeptId = prop.DepartmentId ? prop.DepartmentId.toString() : null;
                const propDeptName = prop.DepartmentName ? prop.DepartmentName.trim() : "";

                let matchDept = false;
                if (propDeptId && allowedDeptIds.includes(propDeptId)) {
                    matchDept = true;
                } else {
                    const matchedDeptsByNames = departments
                        .filter(d => allowedDeptIds.includes(d.Id.toString()))
                        .map(d => d.Name.trim());
                    if (matchedDeptsByNames.includes(propDeptName)) {
                        matchDept = true;
                    }
                }
                if (!matchDept) return;
            }

            const topicLevelStr = prop.TopicLevelName || 'Khác';
            const key = `${userId}_${topicLevelStr}`;

            if (!statsMap.has(key)) {
                statsMap.set(key, {
                    userId: userId,
                    fullName: prop.FullName || 'Chưa xác định',
                    deptName: prop.DepartmentName || 'Chưa xác định',
                    topicLevel: topicLevelStr,
                    count: 0
                });
            }
            statsMap.get(key).count += 1;
        });

        let result = Array.from(statsMap.values());
        result.sort((a, b) => {
            if (b.count !== a.count) return b.count - a.count;
            return a.fullName.localeCompare(b.fullName);
        });

        return result;
    }, [proposals, departments]);

    const aggregatedData = useMemo(() => computeAggregatedData({
        dept: filterDept,
        authorId: filterAuthorId,
        topicLevel: filterTopicLevel,
        year: filterYear,
        status: filterStatus,
        globalSearch: globalSearch
    }), [computeAggregatedData, filterDept, filterAuthorId, filterTopicLevel, filterYear, filterStatus, globalSearch]);

    const totalPages = Math.ceil(aggregatedData.length / itemsPerPage);
    const paginatedData = aggregatedData.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);

    useEffect(() => { setCurrentPage(1); }, [filterDept, filterAuthorId, filterTopicLevel, filterYear, globalSearch, filterStatus]);

    const executeExport = useCallback((dataToExport, fileSuffix) => {
        if (!dataToExport || dataToExport.length === 0) {
            return toast.current?.show({ severity: 'warn', summary: 'Cảnh báo', detail: 'Không có dữ liệu để xuất!', life: 3000 });
        }

        let totalProposals = 0;
        const formattedData = dataToExport.map((item, index) => {
            totalProposals += item.count;
            return {
                'STT': index + 1,
                'Người đề xuất': item.fullName,
                'Đơn vị': item.deptName,
                'Cấp đề tài': item.topicLevel,
                'Số lượng': item.count
            };
        });

        const summaryRow = {
            'STT': '',
            'Người đề xuất': 'TỔNG CỘNG',
            'Đơn vị': `${dataToExport.length} Cá nhân/Nhóm`,
            'Cấp đề tài': '',
            'Số lượng': totalProposals
        };

        const colWidths = [
            { wch: 8 },
            { wch: 35 },
            { wch: 40 },
            { wch: 25 },
            { wch: 15 }
        ];

        downloadExcel({
            data: formattedData,
            fileName: `ThongKeDeXuatTheoCaNhan_${fileSuffix}`,
            sheetName: 'ThongKeDeXuat',
            colWidths: colWidths,
            summaryRow: summaryRow
        });
    }, []);

    const handleExportCurrent = () => {
        executeExport(aggregatedData, 'HienHanh');
        setExportDropdownOpen(false);
    };

    const handlePreviewAdvancedFilters = () => {
        const advancedData = computeAggregatedData(exportFilters);
        setPreviewExportData(advancedData);
    };

    const handleClearAdvancedFilters = () => {
        setExportFilters({ dept: '', userId: '', topicLevel: '', year: '2026', status: '', globalSearch: '' });
        setPreviewExportData(null);
    };

    const handleExportFromModal = () => {
        const dataToExport = previewExportData || computeAggregatedData(exportFilters);
        executeExport(dataToExport, 'LocNangCao');
        setIsExportModalOpen(false);
    };

    const renderDeptTree = (parentId = null, level = 0) => {
        return departments
            .filter(d => d.ParentId === parentId)
            .map(d => (
                <React.Fragment key={`filter-dept-${d.Id}`}>
                    <option value={d.Id}>
                        {'\u00A0'.repeat(level * 4)}{level > 0 ? '˪ ' : ''}{d.Name}
                    </option>
                    {renderDeptTree(d.Id, level + 1)}
                </React.Fragment>
            ));
    };

    return (
        <div className="page-container" style={{ padding: '20px' }}>
            <Toast ref={toast} position="top-right" baseZIndex={99999} style={{ zIndex: 99999 }} />

            <div className="page-header" style={{ marginBottom: '20px' }}>
                <h2 style={{ textTransform: 'uppercase' }}>THỐNG KÊ ĐỀ XUẤT THEO CÁ NHÂN</h2>
            </div>

            <div style={{ display: 'flex', flexDirection: isDesktop ? 'row' : 'column', gap: '10px', marginBottom: '20px' }}>
                <div style={{ position: 'relative', display: isDesktop ? 'inline-block' : 'block', width: isDesktop ? 'auto' : '100%' }}>
                    <button
                        className="btn-add-new"
                        style={{ backgroundColor: '#1a9953', margin: 0, width: '100%' }}
                        onClick={() => setExportDropdownOpen(!exportDropdownOpen)}
                    >
                        <i className="fa-solid fa-file-excel"></i> Xuất Excel <i className="fa-solid fa-caret-down" style={{ marginLeft: '5px' }}></i>
                    </button>

                    {exportDropdownOpen && (
                        <>
                            <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 999 }} onClick={() => setExportDropdownOpen(false)}></div>
                            <div className="custom-dropdown-menu">
                                <div className="custom-dropdown-item" onClick={handleExportCurrent}>
                                    <i className="fa-solid fa-list-check" style={{ color: '#1a9953', width: '20px', fontSize: '16px' }}></i>
                                    Xuất toàn bộ dữ liệu
                                </div>
                                <div className="custom-dropdown-divider"></div>
                                <div className="custom-dropdown-item" onClick={() => {
                                    setIsExportModalOpen(true);
                                    setExportDropdownOpen(false);
                                    setPreviewExportData(null);
                                }}>
                                    <i className="fa-solid fa-filter" style={{ color: '#f39c12', width: '20px', fontSize: '16px' }}></i>
                                    Lọc dữ liệu nâng cao
                                </div>
                            </div>
                        </>
                    )}
                </div>
            </div>

            <div style={{ backgroundColor: '#fff', padding: '20px', borderRadius: '5px', boxShadow: '0 2px 4px rgba(0,0,0,0.05)', marginBottom: '20px' }}>
                <p className="sub-title" style={{ margin: '0 0 15px 0' }}>BỘ LỌC HIỂN THỊ</p>

                <div style={{ display: 'grid', gridTemplateColumns: isDesktop ? 'repeat(3, 1fr)' : 'repeat(auto-fit, minmax(280px, 1fr))', gap: '15px' }}>

                    <select className="form-input" value={filterDept} onChange={(e) => setFilterDept(e.target.value)}>
                        <option value="">Chọn đơn vị</option>
                        {renderDeptTree()}
                    </select>

                    <div style={{ width: '100%' }}>
                        <AutoComplete
                            value={selectedAuthor}
                            suggestions={filteredAuthors}
                            completeMethod={searchAuthor}
                            field="label"
                            dropdown
                            placeholder="Họ tên giảng viên"
                            itemTemplate={userFilterTemplate}
                            onChange={(e) => {
                                setSelectedAuthor(e.value);
                                if (e.value && e.value.value) {
                                    setFilterAuthorId(e.value.value);
                                } else {
                                    setFilterAuthorId(null);
                                }
                            }}
                            style={{ width: '100%', height: '40px' }}
                            inputStyle={{ width: '100%', padding: '0 12px', border: '1px solid #ced4da', borderRadius: '4px', fontFamily: 'inherit' }}
                            panelStyle={{ width: 'auto', minWidth: '350px', maxWidth: '500px' }}
                            emptyMessage="Không tìm thấy giảng viên"
                        />
                    </div>

                    <select className="form-input" value={filterTopicLevel} onChange={(e) => setFilterTopicLevel(e.target.value)}>
                        <option value="">Cấp đề tài</option>
                        {topicLevels.map(tl => <option key={tl.Id} value={tl.Id}>{tl.Name}</option>)}
                    </select>

                    <div style={{ position: 'relative' }}>
                        <select className="form-input" value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)} style={{ width: '100%' }}>
                            <option value="">Tất cả trạng thái</option>
                            <option value="Lưu nháp">Lưu nháp</option>
                            <option value="Đã gửi duyệt">Đã gửi duyệt</option>
                            <option value="Đã duyệt">Đã duyệt</option>
                            <option value="Từ chối">Từ chối</option>
                            <option value="Yêu cầu chỉnh sửa">Yêu cầu chỉnh sửa</option>
                        </select>
                        {filterStatus && <i className="fa-solid fa-xmark" style={{ ...clearIconStyle, right: '30px' }} onClick={() => setFilterStatus('')}></i>}
                    </div>

                    <div style={{ position: 'relative' }}>
                        <input
                            type="text"
                            className="form-input"
                            placeholder="Năm (VD: 2026)"
                            value={filterYear}
                            onChange={(e) => {
                                const val = e.target.value.replace(/\D/g, '');
                                setFilterYear(val);
                            }}
                            style={{ width: '100%', paddingRight: filterYear ? '30px' : '12px' }}
                        />
                        {filterYear && (
                            <i
                                className="fa-solid fa-xmark"
                                style={{ position: 'absolute', right: '12px', top: '50%', transform: 'translateY(-50%)', cursor: 'pointer', color: '#999' }}
                                onClick={() => setFilterYear('')}
                            ></i>
                        )}
                    </div>

                    <div style={{ position: 'relative' }}>
                        <input
                            type="text" className="form-input" placeholder="Tìm kiếm mã / tên đề tài"
                            value={globalSearch} onChange={(e) => setGlobalSearch(e.target.value)}
                            style={{ width: '100%', paddingRight: '35px' }}
                        />
                        <i className="fa-solid fa-magnifying-glass" style={{ position: 'absolute', right: '12px', top: '12px', color: '#888' }}></i>
                    </div>
                </div>
            </div>

            <div style={{ backgroundColor: '#fff', borderRadius: '5px', boxShadow: '0 2px 4px rgba(0,0,0,0.05)', overflowX: 'auto' }}>
                {isLoading ? (
                    <div style={{ padding: '30px', textAlign: 'center', color: '#666' }}>
                        <i className="fa-solid fa-circle-notch fa-spin" style={{ marginRight: '10px' }}></i> Đang tải dữ liệu
                    </div>
                ) : (
                    <>
                        <table className="custom-table" style={{ width: '100%', borderCollapse: 'collapse', minWidth: '900px', margin: 0 }}>
                            <thead style={{ position: 'sticky', top: 0, zIndex: 1 }}>
                                <tr style={{ backgroundColor: '#f4f6f9', borderBottom: '2px solid #ddd' }}>
                                    <th style={thStyle}>STT</th>
                                    <th style={{ ...thStyle, textAlign: 'left' }}>NGƯỜI ĐỀ XUẤT</th>
                                    <th style={{ ...thStyle, textAlign: 'left' }}>ĐƠN VỊ</th>
                                    <th style={{ ...thStyle, textAlign: 'left' }}>CẤP ĐỀ TÀI</th>
                                    <th style={thStyle}>SỐ LƯỢNG</th>
                                </tr>
                            </thead>
                            <tbody>
                                {paginatedData.length > 0 ? (
                                    paginatedData.map((item, index) => (
                                        <tr key={`${item.userId}-${item.topicLevel}`} style={{ borderBottom: '1px solid #eee' }}>
                                            <td style={tdStyle}>{(currentPage - 1) * itemsPerPage + index + 1}</td>
                                            <td style={{ ...tdStyle, textAlign: 'left', fontWeight: '500' }}>{item.fullName}</td>
                                            <td style={{ ...tdStyle, textAlign: 'left', color: '#555' }}>{item.deptName}</td>
                                            <td style={{ ...tdStyle, textAlign: 'left', color: '#555' }}>{item.topicLevel}</td>
                                            <td style={{ ...tdStyle, fontWeight: 'bold', color: '#1a56db' }}>{item.count}</td>
                                        </tr>
                                    ))
                                ) : (
                                    <tr>
                                        <td colSpan="5" style={{ padding: '40px', textAlign: 'center', color: '#888' }}>
                                            <i className="fa-solid fa-folder-open" style={{ fontSize: '24px', display: 'block', marginBottom: '10px', color: '#ccc' }}></i>
                                            Không có dữ liệu đề xuất phù hợp
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>

                        {totalPages > 1 && (
                            <div style={{ display: 'flex', justifyContent: 'center', padding: '15px', gap: '5px' }}>
                                <button onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))} disabled={currentPage === 1} style={paginationBtnStyle(currentPage === 1)}>
                                    <i className="fa-solid fa-chevron-left"></i>
                                </button>
                                {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
                                    <button key={page} onClick={() => setCurrentPage(page)} style={page === currentPage ? activePaginationBtnStyle : paginationBtnStyle(false)}>
                                        {page}
                                    </button>
                                ))}
                                <button onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))} disabled={currentPage === totalPages} style={paginationBtnStyle(currentPage === totalPages)}>
                                    <i className="fa-solid fa-chevron-right"></i>
                                </button>
                            </div>
                        )}
                    </>
                )}
            </div>

            {isExportModalOpen && (
                <div className="modal-overlay" style={{ zIndex: 900 }}>
                    <div className="modal-box form-modal-box" style={{ maxWidth: '800px', maxHeight: '90vh', display: 'flex', flexDirection: 'column' }}>
                        <div className="modal-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                            <h3 style={{ margin: 0, paddingRight: '15px', lineHeight: '1.4' }}>Bộ Lọc & Xuất Excel nâng cao</h3>
                            <button className="close-btn" onClick={() => setIsExportModalOpen(false)} style={{ fontSize: '26px', lineHeight: '1', display: 'flex', flexShrink: 0, marginTop: '-2px' }}>&times;</button>
                        </div>

                        <div className="modal-body" style={{ padding: '20px', overflowY: 'auto' }}>
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '15px', marginBottom: '15px' }}>
                                <div className="form-group" style={{ flex: '1 1 200px' }}>
                                    <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>Lọc theo Đơn vị</label>
                                    <select className="form-input" style={{ width: '100%', padding: '8px', border: '1px solid #ccc', borderRadius: '4px' }}
                                        value={exportFilters.dept} onChange={(e) => setExportFilters({ ...exportFilters, dept: e.target.value })}>
                                        <option value="">Tất cả Đơn vị</option>
                                        {renderDeptTree()}
                                    </select>
                                </div>
                                <div className="form-group" style={{ flex: '1 1 200px' }}>
                                    <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>Cấp đề tài</label>
                                    <select className="form-input" style={{ width: '100%', padding: '8px', border: '1px solid #ccc', borderRadius: '4px' }}
                                        value={exportFilters.topicLevel} onChange={(e) => setExportFilters({ ...exportFilters, topicLevel: e.target.value })}>
                                        <option value="">Tất cả Cấp</option>
                                        {topicLevels.map(tl => <option key={`exp-tl-${tl.Id}`} value={tl.Id}>{tl.Name}</option>)}
                                    </select>
                                </div>
                                <div className="form-group" style={{ flex: '1 1 200px' }}>
                                    <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>Trạng thái</label>
                                    <select className="form-input" style={{ width: '100%', padding: '8px', border: '1px solid #ccc', borderRadius: '4px' }}
                                        value={exportFilters.status} onChange={(e) => setExportFilters({ ...exportFilters, status: e.target.value })}>
                                        <option value="">Tất cả trạng thái</option>
                                        <option value="Lưu nháp">Lưu nháp</option>
                                        <option value="Đã gửi duyệt">Đã gửi duyệt</option>
                                        <option value="Đã duyệt">Đã duyệt</option>
                                        <option value="Từ chối">Từ chối</option>
                                        <option value="Yêu cầu chỉnh sửa">Yêu cầu chỉnh sửa</option>
                                    </select>
                                </div>
                            </div>

                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '15px', marginBottom: '15px' }}>
                                <div className="form-group" style={{ flex: '1 1 200px' }}>
                                    <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>Năm đề xuất</label>
                                    <input type="text" className="form-input" style={{ width: '100%', padding: '8px', border: '1px solid #ccc', borderRadius: '4px' }}
                                        placeholder="VD: 2026"
                                        value={exportFilters.year} onChange={(e) => {
                                            const val = e.target.value.replace(/\D/g, '');
                                            setExportFilters({ ...exportFilters, year: val });
                                        }}
                                    />
                                </div>
                                <div className="form-group" style={{ flex: '1 1 200px' }}>
                                    <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>Giảng viên</label>
                                    <SearchableFocusDropdown
                                        value={exportFilters.userId}
                                        options={userOptions}
                                        onChange={(e) => setExportFilters({ ...exportFilters, userId: e.value })}
                                        placeholder="Chọn giảng viên"
                                        panelUniqueClass="custom-dropdown-panel-proposalstats-export-advanced-filter"
                                        className="custom-dropdown"
                                        filterBy="label,searchKey"
                                        optionLabel="label"
                                    />
                                </div>
                                <div className="form-group" style={{ flex: '1 1 200px' }}>
                                    <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>Mã / Tên đề tài</label>
                                    <input type="text" className="form-input" style={{ width: '100%', padding: '8px', border: '1px solid #ccc', borderRadius: '4px' }}
                                        placeholder="Tìm theo tên đề tài"
                                        value={exportFilters.globalSearch} onChange={(e) => setExportFilters({ ...exportFilters, globalSearch: e.target.value })}
                                    />
                                </div>
                            </div>

                            {previewExportData && (
                                <div style={{ marginTop: '10px', borderTop: '1px solid #eee', paddingTop: '15px' }}>
                                    <p style={{ margin: '0 0 10px 0', fontWeight: 'bold', color: '#1a9953' }}>
                                        <i className="fa-solid fa-table-list"></i> Xem trước dữ liệu xuất:
                                        ({previewExportData.length} cá nhân/nhóm)
                                    </p>
                                    <div style={{ maxHeight: '250px', overflowY: 'auto', overflowX: 'auto', border: '1px solid #ddd', borderRadius: '4px' }}>
                                        <table className="custom-table" style={{ width: '100%', minWidth: '600px', fontSize: '13px', margin: 0 }}>
                                            <thead style={{ position: 'sticky', top: 0, backgroundColor: '#f8f9fa', zIndex: 2 }}>
                                                <tr>
                                                    <th width="50px" style={{ textAlign: 'center' }}>STT</th>
                                                    <th style={{ textAlign: 'left' }}>NGƯỜI ĐỀ XUẤT</th>
                                                    <th style={{ textAlign: 'left' }}>ĐƠN VỊ</th>
                                                    <th style={{ textAlign: 'left' }}>CẤP ĐỀ TÀI</th>
                                                    <th width="80px" style={{ textAlign: 'center' }}>SỐ LƯỢNG</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {previewExportData.length > 0 ? (
                                                    previewExportData.map((row, idx) => (
                                                        <tr key={`preview-${row.userId}-${row.topicLevel}`}>
                                                            <td style={{ textAlign: 'center' }}>{idx + 1}</td>
                                                            <td style={{ textAlign: 'left' }}>{row.fullName}</td>
                                                            <td style={{ textAlign: 'left' }}>{row.deptName}</td>
                                                            <td style={{ textAlign: 'left' }}>{row.topicLevel}</td>
                                                            <td style={{ textAlign: 'center', color: '#1a56db', fontWeight: 'bold' }}>{row.count}</td>
                                                        </tr>
                                                    ))
                                                ) : (
                                                    <tr>
                                                        <td colSpan={5} style={{ textAlign: 'center', padding: '20px', color: '#888' }}>
                                                            Không có dữ liệu khớp với bộ lọc
                                                        </td>
                                                    </tr>
                                                )}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            )}
                        </div>

                        <div className="modal-footer" style={{ borderTop: '1px solid #eee', padding: '15px 20px', display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', gap: '10px' }}>
                            <button type="button" className="btn-cancel" style={{ border: 'none', background: 'none', color: '#e74c3c', margin: 0 }} onClick={handleClearAdvancedFilters}>
                                <i className="fa-solid fa-eraser"></i> Xóa bộ lọc
                            </button>
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px' }}>
                                <button type="button" className="btn-submit" style={{ backgroundColor: '#3498db', margin: 0 }} onClick={handlePreviewAdvancedFilters}>
                                    <i className="fa-solid fa-magnifying-glass"></i> Lọc & Xem trước
                                </button>
                                <button type="button" className="btn-submit" style={{ backgroundColor: '#1a9953', margin: 0 }} onClick={handleExportFromModal}>
                                    <i className="fa-solid fa-download"></i> Xuất Excel
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

const thStyle = { padding: '15px 12px', textAlign: 'center', color: '#1a56db', fontSize: '13px', fontWeight: 'bold' };
const tdStyle = { padding: '15px 12px', textAlign: 'center', fontSize: '14px', color: '#333' };
const clearIconStyle = { position: 'absolute', right: '10px', top: '12px', cursor: 'pointer', color: '#999', fontSize: '12px' };

const paginationBtnStyle = (disabled) => ({
    padding: '6px 14px', border: '1px solid #ddd', backgroundColor: disabled ? '#f5f5f5' : '#fff',
    color: disabled ? '#aaa' : '#333', cursor: disabled ? 'not-allowed' : 'pointer', borderRadius: '4px', fontSize: '13px'
});
const activePaginationBtnStyle = { ...paginationBtnStyle(false), backgroundColor: '#0284c7', color: '#fff', border: '1px solid #0284c7', fontWeight: 'bold' };

export default ProposalStatsByIndividual;